import type {
  SentryFrontendConfig,
  SentrySDK,
  SentryTelemetryContext,
} from './types';
import { applySentryContext } from './context';
import { shouldDropFrontendError } from './filters';
import { parseSentrySampleRate } from './sampling';
import { scrubUrl } from './scrubbers';

export interface FrontendSentryEnv {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: string;
  enabled?: string;
}

function normalizeDsn(dsn: string | undefined): string {
  return dsn?.trim() ?? '';
}

function hasValidSentryProjectId(dsn: string): boolean {
  if (!dsn) {
    return false;
  }

  try {
    const url = new URL(dsn);
    const projectId = url.pathname.split('/').filter(Boolean).at(-1);
    return projectId !== undefined && /^\d+$/u.test(projectId);
  } catch {
    return false;
  }
}

type FrontendSentryEvent = {
  exception?: { values?: Array<{ value?: string }> };
  request?: { url?: string; headers?: Record<string, string> };
  [key: string]: unknown;
};

function asFrontendSentryEvent(event: unknown): FrontendSentryEvent | null {
  if (!event || typeof event !== 'object') {
    return null;
  }

  return event as FrontendSentryEvent;
}

export interface FrontendHttpFailure {
  app: string;
  surface: SentryFrontendConfig['surface'];
  method: string;
  path: string;
  url: string;
  stage: 'network' | 'http' | 'response_parse';
  statusCode?: number;
  errorCode?: string | null;
  requestId?: string | null;
  responseContentType?: string | null;
}

function isNetworkUnavailableError(error: unknown): boolean {
  return error instanceof Error && error.name === 'NetworkUnavailableError';
}

export function parseFrontendSentryConfig(
  env: FrontendSentryEnv,
  surface: SentryFrontendConfig['surface'],
  app: string,
): SentryFrontendConfig {
  const dsn = normalizeDsn(env.dsn);
  const enabled =
    env.enabled !== undefined
      ? env.enabled !== 'false' && env.enabled !== '0'
      : false;
  const validDsn = hasValidSentryProjectId(dsn);

  if (enabled && dsn && !validDsn && typeof console !== 'undefined') {
    console.warn(
      `[sentry] Skipping initialization for ${app}: SENTRY_DSN is missing a numeric project id.`,
    );
  }

  return {
    dsn,
    environment: env.environment ?? 'development',
    release: env.release ?? '0.0.0',
    tracesSampleRate: parseSentrySampleRate(env.tracesSampleRate),
    enabled: enabled && validDsn,
    surface,
    app,
  };
}

export function buildFrontendSentryOptions(config: SentryFrontendConfig) {
  return {
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: config.tracesSampleRate,
    enabled: config.enabled,
    sendDefaultPii: false,
    initialScope: {
      tags: {
        app: config.app,
        surface: config.surface,
        runtime: 'browser' as const,
      },
    },
    beforeSend(event: unknown) {
      const sentryEvent = asFrontendSentryEvent(event);
      const message = sentryEvent?.exception?.values?.[0]?.value ?? '';

      if (shouldDropFrontendError(message, config.surface)) {
        return null;
      }

      if (sentryEvent?.request?.url) {
        sentryEvent.request.url =
          scrubUrl(sentryEvent.request.url) ?? sentryEvent.request.url;
      }

      return event;
    },
  };
}

export function setFrontendSentryContext(
  sentry: SentrySDK,
  context: SentryTelemetryContext,
): void {
  applySentryContext(sentry, context);
}

export function captureExceptionSafe(
  sentry: SentrySDK,
  error: unknown,
  context?: SentryTelemetryContext,
): string {
  let eventId = '';
  sentry.withScope((scope) => {
    if (context) {
      applySentryContext(scope, context);
    }
    eventId = sentry.captureException(error);
  });
  return eventId;
}

export function captureMessageSafe(
  sentry: SentrySDK,
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: SentryTelemetryContext,
): string {
  let eventId = '';
  sentry.withScope((scope) => {
    if (context) {
      applySentryContext(scope, context);
    }
    eventId = sentry.captureMessage(message, level);
  });
  return eventId;
}

export function captureFrontendHttpFailure(
  sentry: SentrySDK,
  error: unknown,
  input: FrontendHttpFailure,
): string {
  if (input.stage === 'network' && isNetworkUnavailableError(error)) {
    return '';
  }

  let eventId = '';

  sentry.withScope((scope) => {
    scope.setLevel(
      input.stage === 'http' && (input.statusCode ?? 500) < 500
        ? 'warning'
        : 'error',
    );
    scope.setTag('app', input.app);
    scope.setTag('surface', input.surface);
    scope.setTag('runtime', 'browser');
    scope.setTag('http_stage', input.stage);
    scope.setTag('http_method', input.method);

    if (input.statusCode !== undefined) {
      scope.setTag('http_status', String(input.statusCode));
    }

    if (input.errorCode) {
      scope.setTag('error_code', input.errorCode);
    }

    scope.setContext('http_request', {
      method: input.method,
      path: input.path,
      url: scrubUrl(input.url) ?? input.url,
      stage: input.stage,
      status_code: input.statusCode ?? null,
      error_code: input.errorCode ?? null,
      request_id: input.requestId ?? null,
      response_content_type: input.responseContentType ?? null,
    });

    eventId = sentry.captureException(error);
  });

  return eventId;
}
